// Paper and ink shared by the fog layer on the land (fog.ts) and the fog veil on the sea (seaVeil.ts).
//
//   overlay texel (g, b) ──► bary blend of 3 hex centres ──► (now, prev) in bytes
//   byte + noise jitter ──► smoothstep ──► cover 0..1                (ragged, never hex-shaped)
//   reveal 0 → 1 over ~1 s ──► per-pixel dissolve prev → now, with a dark burn rim at the front
//
// The paper is procedural (noise, periodic across the world): the terrain shader has no free texture
// binding for public/assets/textures/fog/parchment.webp (16 sampled textures per stage is the limit).

import { float, mix, smoothstep, textureLoad, vec2 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { rgb, screen } from '../tsl/color.ts';
import { cellCoord, hexBary, type HexFrag } from '../tsl/hex.ts';
import { fbmW, strokesW } from './wrapNoise.ts';
import type { OverlayData } from './overlayData.ts';

type F = Node<'float'>;
type V2 = Node<'vec2'>;
type V3 = Node<'vec3'>;

const BYTE = 255;

// Paper: three tones by mottling, fibre streaks, soft stains. Detail fades once a pixel spans
// more than a fibre, so far views stay calm.
const PAPER_TONES = { dark: '#d2bd8e', mid: '#ead9ae', light: '#f8f0d6' } as const;
const MOTTLE_FREQ = 0.85;
const FIBRE_FREQ = 6;
const FIBRE_SHARE = 0.5;
const GRAIN_PX = [0.3, 0.1] as const;

// Coverage: bytes below FOG_FROM are clear, above FOG_FULL are full paper (ages in fogField.ts).
export const FOG_FROM = 32;
export const FOG_FULL = 224;
/** Below this byte the fog is too faint to see even with the jitter: the shader skips the hex. */
export const FOG_ACTIVE = 8;
/** Bytes of noise either side of the threshold, so the fog edge is ragged like torn paper. */
const FOG_JITTER = 70;
const FOG_NOISE_FREQ = 0.55;

// Burn edge: a dissolve front that sweeps the noise field as the reveal runs 0 → 1.
const BURN_FREQ = 0.32;
const BURN_WIDTH = 0.16;
export const BURN_INK = '#2b1a0c';
export const BURN_EMBER = '#c8621c';

// Paper tone: soft stains, and older (staler) sheets are browner.
const STAIN_FREQ = 0.09;
const STAIN_DARK = 0.86;
const STAIN_LIGHT = 1.06;
const AGED_TINT = '#d2b98c';
const AGED_SHARE = 0.35;
/** The aged tint is a swatch (mid tones); scaled so a full share keeps the sheet's brightness. */
const AGED_BOOST = 1.5;

export const INK = '#3a2617';

/** Paper colour at world XZ (`px`: world units per pixel); `age` 0..1 browns it. */
export function paperAt(xz: V2, px: F, age: F): V3 {
  const detail = smoothstep(GRAIN_PX[0], GRAIN_PX[1], px);
  const mottle = mix(float(0.5), fbmW(xz, MOTTLE_FREQ), detail);
  const fibre = mix(float(0.5), strokesW(xz.add(vec2(0, 9.3)), FIBRE_FREQ), detail);
  const stain = fbmW(xz.add(vec2(0, 5.7)), STAIN_FREQ).mul(STAIN_LIGHT - STAIN_DARK).add(STAIN_DARK);

  const tone = mix(screen(PAPER_TONES.dark), screen(PAPER_TONES.mid), smoothstep(0.2, 0.8, mottle));
  const sheet = mix(tone, screen(PAPER_TONES.light), smoothstep(0.55, 0.9, fibre).mul(FIBRE_SHARE)).mul(stain);
  return mix(sheet, sheet.mul(rgb(AGED_TINT).mul(AGED_BOOST)), age.mul(AGED_SHARE));
}

/** (now, prev) fog field in bytes at a fragment: linear between the three nearest hex centres. */
export function fieldAt(data: OverlayData, frag: HexFrag): Node<'vec2'> {
  const { cells, w } = hexBary(frag);
  const texel = cells.map((c) => textureLoad(data.texture, cellCoord(c)));
  const now = texel[0].y.mul(w.x).add(texel[1].y.mul(w.y)).add(texel[2].y.mul(w.z));
  const prev = texel[0].z.mul(w.x).add(texel[1].z.mul(w.y)).add(texel[2].z.mul(w.z));
  return vec2(now, prev).mul(BYTE);
}

/** Cover 0..1 of a fog byte (already jittered). */
function cover(byte: F): F {
  return smoothstep(FOG_FROM, FOG_FULL, byte);
}

export interface FogLook {
  /** Paper cover 0..1 after the dissolve. */
  cover: F;
  /** 1 on the burning front where fog is lifting, else 0. */
  rim: F;
}

/** Cover and burn rim from the field bytes, the reveal uniform and world XZ. Uses noise only. */
export function fogLook(field: Node<'vec2'>, xz: V2, reveal: F): FogLook {
  const jitter = fbmW(xz, FOG_NOISE_FREQ).sub(0.5).mul(FOG_JITTER);
  const now = cover(field.x.add(jitter));
  const prev = cover(field.y.add(jitter));

  // local runs 0 → 1 per pixel, later where the burn noise is high: the front wanders.
  const front = fbmW(xz.add(vec2(0, 31.7)), BURN_FREQ);
  const local = reveal.mul(1 + BURN_WIDTH).sub(front).div(BURN_WIDTH).clamp(0, 1);
  const lifting = smoothstep(0.02, 0.25, prev.sub(now));
  const rim = local.mul(float(1).sub(local)).mul(4).mul(lifting);
  return { cover: prev.add(now.sub(prev).mul(local)), rim };
}
